import { useEffect, useMemo, useState } from "react";
import { TrendingUp, TrendingDown, Equal } from "lucide-react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Area,
  AreaChart,
  BarChart,
  Bar,
} from "recharts";
import {
  ReferenceLine,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";

import { useLanguageMetrics, type MetricData } from "@/hooks/api/useMetricsApi";
import {
  ChartSkeleton,
  RadarChartSkeleton,
  MetricCardSkeleton,
  OverallChartSkeleton,
} from "@/components/pages/metrics/chart-skeleton";
import { MetricColorNumber } from "@/components/common/metrics-color-number";
import {
  fillInMissingData,
  MetricsList,
  periodAverageData,
  periodNormalizedTotalData,
  roundToDecimalsFixed,
  computeStreak,
} from "@/lib/metrics";
import { formatMillisecondsToMMSS } from "@/lib/time";
import { useTranslation } from "react-i18next";
import { SearchSelectGroup } from "@/components/common/search-select-group";
import { DateRange } from "react-day-picker";
import {
  ErrorFetchingData,
  NoDataFound,
} from "@/components/common/user-messages";
import {
  StreakCard,
  StreakCardProps,
} from "@/components/pages/metrics/streak-card";
import { useLearnLanguage } from "@/context/learn-language-context";

export default function LanguageMetricsDashboard() {
  const { t } = useTranslation(["pages", "error"]);
  const {
    languageProficiency,
    isLoading: isLanguageLoading,
    isError: isLanguageError,
  } = useLearnLanguage();

  const [period, setPeriod] = useState("all");
  const [selectedMode, setSelectedMode] = useState("all"); // New state for mode
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [noData, setNoData] = useState(false);

  const chartConfig = MetricsList.reduce(
    (acc, metric) => {
      acc[metric.key] = {
        label: t(metric.label),
      };
      return acc;
    },
    {} as Record<string, { label: string }>,
  );

  const {
    data: metricsData,
    isLoading: isDataLoading,
    error: isDataError,
  } = useLanguageMetrics(
    period,
    selectedMode,
    languageProficiency?.language?.key,
  );

  const { data: streakData } = useLanguageMetrics(
    "all",
    "all",
    languageProficiency?.language?.key,
  );

  useEffect(() => {
    if (isDataLoading || isLanguageLoading) {
      setNoData(false);
      return;
    }
    if (!metricsData?.data || metricsData?.data.length === 0) {
      setNoData(true);
    } else {
      setNoData(false);
    }
  }, [metricsData, isDataLoading]);

  // To be used by metrics sumary cards and radar chart
  const averagePeriodData = useMemo(() => {
    return periodAverageData(metricsData?.data || []);
  }, [metricsData]);

  // To be used by specific metric chart
  const nullFilledData = useMemo(() => {
    return fillInMissingData(metricsData?.data || []);
  }, [metricsData]);

  // To be used by specific metric chart
  const nullFilledStreakData = useMemo(() => {
    return fillInMissingData(streakData?.data || []);
  }, [streakData]);

  // To be used by total aggregated metric chart
  const normalizedTotalPeriodData = useMemo(() => {
    return periodNormalizedTotalData(nullFilledData || []);
  }, [nullFilledData]);

  // Calculate summary statistics
  const calculateStats = (metric: string, data: MetricData[]) => {
    const values = data.map((d) => d[metric as keyof MetricData] as number);
    const current = values[values.length - 1];
    const average = roundToDecimalsFixed(
      values.reduce((a, b) => a + b, 0) / values.length,
      1,
    );
    const initial = values[0] || current;
    const change = ((current - initial) / initial) * 100;

    return { current, average, change };
  };

  const getRadarData = () => {
    return MetricsList.map((metric) => ({
      metric: metric.label,
      initial:
        metricsData?.data && metricsData.data.length > 0
          ? (metricsData.data[0][metric.key as keyof MetricData] as number)
          : 0,
      avg:
        metricsData?.data && metricsData.data.length > 0
          ? (averagePeriodData[metric.key as keyof MetricData] as number)
          : 0,
      fullMark: 100,
    }));
  };

  // Compute streak data
  const getStreakData: StreakCardProps = useMemo(() => {
    return computeStreak(nullFilledStreakData);
  }, [nullFilledStreakData]);

  // Custom tick formatter for Y-axis
  const formatYAxisTick = (value: number): string => {
    return formatMillisecondsToMMSS(value);
  };

  if (isDataError || isLanguageError) {
    return <ErrorFetchingData />;
  }

  return (
    <div className="min-h-screen bg-background p-4 mt-12 md:p-10 lg:p-12">
      <div className="mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-3xl font-bold tracking-tight">
              {t("pages:dashboard.header")}
            </h1>
            <p className="hidden md:inline text-muted-foreground">
              {t("pages:dashboard.subheader")}
            </p>
          </div>
          <SearchSelectGroup
            setPeriod={setPeriod}
            setMode={setSelectedMode}
            setDate={setDateRange}
          />
        </div>

        {noData && <NoDataFound />}

        {/* General Summary Section with Streak */}
        <div className="grid gap-6 lg:grid-cols-4">
          <>
            {isDataLoading || isLanguageLoading ? (
              <RadarChartSkeleton />
            ) : (
              <StreakCard
                currentStreak={getStreakData.currentStreak}
                todayTimeSpent={getStreakData.todayTimeSpent}
                requiredTimeForStreak={getStreakData.requiredTimeForStreak}
                lastActiveDate={getStreakData.lastActiveDate}
              />
            )}
          </>
          <div className="grid gap-3 grid-cols-1 col-span-2">
            {isDataLoading || isLanguageLoading ? (
              <OverallChartSkeleton />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>
                    {t("pages:dashboard.charts.totalScore.title")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ChartContainer
                    config={{ total: { label: "Total" } }}
                    className="pt-10 h-[185px]"
                  >
                    <AreaChart
                      data={normalizedTotalPeriodData}
                      margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                    >
                      <defs>
                        <linearGradient
                          id="areaGradient"
                          x1="0"
                          y1="1"
                          x2="0"
                          y2="0"
                        >
                          <stop
                            offset="0%"
                            stopColor="hsl(var(--metric-red))"
                            stopOpacity={0}
                          />
                          <stop
                            offset="50%"
                            stopColor="hsl(var(--metric-yellow))"
                            stopOpacity={0.6}
                          />
                          <stop
                            offset="100%"
                            stopColor="hsl(var(--metric-green))"
                            stopOpacity={1}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                      />
                      <YAxis
                        domain={[0, 100]}
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                      />
                      <ChartTooltip
                        wrapperStyle={{ width: "190px" }}
                        content={<ChartTooltipContent />}
                        cursor={{ strokeDasharray: "3 3" }}
                      />
                      <Area
                        dataKey="total"
                        stroke="hsl(var(--primary))"
                        fill="url(#areaGradient)"
                        type="monotone"
                        fillOpacity={0.2}
                        connectNulls // Problematic to stack and connect nulls
                      />
                    </AreaChart>
                  </ChartContainer>
                </CardContent>
              </Card>
            )}
            {isDataLoading || isLanguageLoading ? (
              <OverallChartSkeleton />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>
                    {t("pages:dashboard.charts.totalDuration.title")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ChartContainer
                    config={{ duration: { label: "Duration" } }}
                    className="pt-10 h-[185px]"
                  >
                    <BarChart
                      data={nullFilledData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                      />
                      <YAxis
                        domain={[0, 10]}
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                        tickFormatter={formatYAxisTick}
                      />
                      <ChartTooltip
                        wrapperStyle={{ width: "180px" }}
                        content={<ChartTooltipContent />}
                        cursor={{ strokeDasharray: "3 3" }}
                        formatter={(value: number) =>
                          formatMillisecondsToMMSS(value)
                        }
                      />
                      <ReferenceLine
                        y={getStreakData.requiredTimeForStreak * 60 * 1000} // Convert minutes to milliseconds
                        stroke="#10b981"
                        strokeWidth={2}
                        strokeDasharray="3 3"
                        label={{
                          value: t("pages:dashboard.streak.targetStreakTime"),
                          position: "insideTopLeft",
                        }}
                      />
                      <Bar
                        dataKey="duration"
                        stroke="hsl(var(--primary))"
                        fill="hsl(var(--primary))"
                        type="monotone"
                        fillOpacity={0.2}
                        stackId="1"
                      />
                    </BarChart>
                  </ChartContainer>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Metrics Summary Section with Radar Chart */}
        <div className="grid gap-6 lg:grid-cols-4">
          {/* Radar Chart */}
          {isDataLoading || isLanguageLoading ? (
            <RadarChartSkeleton />
          ) : (
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>
                  {t("pages:dashboard.charts.radarSummary.title")}
                </CardTitle>
                <CardDescription>
                  {t("pages:dashboard.charts.radarSummary.description")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer
                  config={chartConfig}
                  className="h-[280px] md:h-[380px] w-full"
                >
                  <RadarChart
                    data={metricsData && metricsData.data ? getRadarData() : []}
                    margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                  >
                    <defs>
                      <radialGradient
                        id="centeredRadialGradient"
                        cx="50%"
                        cy="50%"
                        gradientUnits="userSpaceOnUse"
                        r="22%"
                      >
                        <stop offset="0%" stopColor="hsl(var(--metric-red))" />
                        <stop
                          offset="50%"
                          stopColor="hsl(var(--metric-yellow))"
                        />
                        <stop
                          offset="100%"
                          stopColor="hsl(var(--metric-green))"
                        />
                      </radialGradient>
                    </defs>
                    <PolarGrid gridType="circle" />
                    <PolarAngleAxis
                      dataKey="metric"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(value) => t(value as string)}
                    />
                    <PolarRadiusAxis domain={[0, 100]} tick={false} />
                    <ChartTooltip
                      content={<ChartTooltipContent />}
                      labelFormatter={(value) => t(value as string)}
                    />
                    <Radar
                      name="Average"
                      dataKey="avg"
                      stroke="url(#centeredRadialGradient)"
                      dot={{ r: 4, fill: "url(#centeredRadialGradient)" }}
                      fillOpacity={0}
                      strokeWidth={2}
                    />
                    <Radar
                      name="Initial"
                      dataKey="initial"
                      stroke="url(#centeredRadialGradient)"
                      dot={{ r: 4, fill: "url(#centeredRadialGradient)" }}
                      fillOpacity={0}
                      strokeWidth={2}
                      strokeOpacity={0.4}
                    />
                  </RadarChart>
                </ChartContainer>
              </CardContent>
            </Card>
          )}

          {/* Summary Cards */}
          <div className="grid gap-3 grid-cols-2 md:gap-4 lg:col-span-2">
            {isDataLoading || isLanguageLoading
              ? Array.from({ length: 6 }).map((_, index) => (
                  <MetricCardSkeleton key={index} />
                ))
              : metricsData &&
                MetricsList.map((metric) => {
                  const stats = calculateStats(metric.key, metricsData.data);
                  const IconComponent = metric.icon;
                  return (
                    <Card key={metric.key}>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <IconComponent className="h-4 w-4 text-muted-foreground" />
                            <CardTitle className="text-sm font-medium">
                              {t(metric.label)}
                            </CardTitle>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {t(metric.description)}
                          </p>
                        </div>
                        {stats.change > 0 ? (
                          <TrendingUp className="h-4 w-4 text-green-600" />
                        ) : stats.change < 0 ? (
                          <TrendingDown className="h-4 w-4 text-destructive" />
                        ) : (
                          <Equal className="h-4 w-4 text-blue-600" />
                        )}
                      </CardHeader>
                      {!noData && (
                        <CardContent className="flex justify-between pt-4 px-2 pb-2">
                          <div>
                            <MetricColorNumber
                              value={stats.average}
                              size="md"
                            />
                          </div>
                          {stats.change !== 0 && (
                            <p className="text-xs text-muted-foreground text-right pl-2">
                              {stats.change > 0 ? "+" : ""}
                              {stats.change.toFixed(1)}%
                              <br />
                              {/* {t(
                                "pages:dashboard.charts.cardSummary.compared-to",
                              )} */}
                            </p>
                          )}
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid gap-6 grid-cols-2">
          {isDataLoading || isLanguageLoading
            ? Array.from({ length: 6 }).map((_, index) => (
                <ChartSkeleton key={index} />
              ))
            : MetricsList.map((metric) => {
                const IconComponent = metric.icon;
                return (
                  <Card key={metric.key}>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <IconComponent className="h-5 w-5 text-muted-foreground" />
                        <CardTitle>{t(metric.label)}</CardTitle>
                      </div>
                      <CardDescription>{t(metric.description)}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ChartContainer
                        config={chartConfig}
                        className="h-[200px]"
                      >
                        <AreaChart
                          data={nullFilledData}
                          margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                        >
                          <defs>
                            <linearGradient
                              id="areaGradient"
                              x1="0"
                              y1="1"
                              x2="0"
                              y2="0"
                            >
                              <stop
                                offset="0%"
                                stopColor="hsl(var(--metric-red))"
                                stopOpacity={0}
                              />
                              <stop
                                offset="50%"
                                stopColor="hsl(var(--metric-yellow))"
                                stopOpacity={0.6}
                              />
                              <stop
                                offset="100%"
                                stopColor="hsl(var(--metric-green))"
                                stopOpacity={1}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                          />
                          <YAxis
                            domain={[0, 100]}
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                          />
                          <ChartTooltip
                            wrapperStyle={{ width: "180px" }}
                            content={<ChartTooltipContent />}
                            cursor={{ strokeDasharray: "3 3" }}
                          />
                          <Area
                            type="monotone"
                            dataKey={metric.key}
                            stroke="hsl(var(--primary))"
                            fill="url(#areaGradient)"
                            fillOpacity={0.3}
                            strokeWidth={2}
                            connectNulls
                          />
                        </AreaChart>
                      </ChartContainer>
                    </CardContent>
                  </Card>
                );
              })}
        </div>
      </div>
    </div>
  );
}
